/**
 * ForgotPassword — pantalla para solicitar reset de contraseña.
 * El usuario ingresa su email; backend genera token y envía correo si existe.
 * Por seguridad, siempre mostramos el mismo mensaje (anti-enumeration).
 * Mismo estilo visual que Login (gradient border + branding).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../utils/api';
import MarcoAcceso from '../components/MarcoAcceso';
import { useTextosAcceso } from '../utils/idiomaAcceso';

function getTenantSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length > 2 || (parts.length === 2 && parts[1] === 'localhost')) return parts[0];
  return localStorage.getItem('tenantSlug') || 'ews';
}

export default function ForgotPassword() {
  const t = useTextosAcceso();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error(t.faltaCorreo); return; }
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim(), getTenantSlug());
      setDone(true);
    } catch (err) {
      // Backend siempre devuelve 200 (anti-enumeration). Si llegamos acá es red/server.
      toast.error(t.errorConexion);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarcoAcceso>
            {!done ? (
              <>
                <div className="mb-6">
                  <h2>{t.olvidoTitulo}</h2>
                  <p className="bajada" style={{ marginBottom: 0 }}>{t.olvidoBajada}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label>{t.correo}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                      autoComplete="email"
                      placeholder={t.correoEj}
                      className="campo"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="boton"
                  >
                    {submitting ? (
                      <>
                        <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t.enviando}
                      </>
                    ) : (
                      <>
                        {t.enviar}
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login" className="olvido inline-flex items-center gap-1.5">
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t.volver}
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-2">
                <div className="inline-flex p-3 rounded-full bg-emerald-100 mb-4">
                  <CheckCircle className="h-9 w-9 text-emerald-600" />
                </div>
                <h2>{t.revise}</h2>
                <p className="text-sm text-gray-600">{t.reviseTexto(email)}</p>
                <p className="text-xs text-gray-500 mt-3">{t.expira}</p>
                <Link to="/login" className="olvido mt-6 inline-flex items-center gap-1.5">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t.volver}
                </Link>
              </div>
            )}
    </MarcoAcceso>
  );
}
